/**
 * KAN-755: мультивыбор товаров каталога: поиск по названию или артикулу (с
 * задержкой, запрос только после ввода), клик по найденному товару добавляет
 * или убирает его, выбранные показаны чипами с крестиком.
 */
import { useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useCatalogProducts } from '@/hooks/useCatalog';
import { useDebounce } from '@/hooks/useDebounce';
import { formatPrice } from '@/lib/catalogTree';
import type { DiscountProductRef } from '@/types/discount';

const SEARCH_DEBOUNCE_MS = 300;
const RESULTS_LIMIT = 10;

interface ProductMultiPickerProps {
  value: DiscountProductRef[];
  onChange: (value: DiscountProductRef[]) => void;
  disabled?: boolean;
  /** Префикс data-testid. */
  testId?: string;
}

export function ProductMultiPicker({ value, onChange, disabled = false, testId = 'product-picker' }: ProductMultiPickerProps) {
  const [input, setInput] = useState('');
  const search = useDebounce(input.trim(), SEARCH_DEBOUNCE_MS);
  const products = useCatalogProducts({ search, page_size: RESULTS_LIMIT }, search.length > 0);
  const results = products.data?.results ?? [];
  const selected = new Set(value.map((p) => String(p.id)));

  const toggle = (product: DiscountProductRef): void => {
    const key = String(product.id);
    onChange(
      selected.has(key)
        ? value.filter((p) => String(p.id) !== key)
        : [...value, { id: product.id, name: product.name, sku: product.sku ?? null, price: product.price ?? null }],
    );
  };

  let found: JSX.Element | null = null;
  if (search.length > 0) {
    if (products.isLoading) found = <p className="text-sm text-gray-500">Поиск...</p>;
    else if (products.isError) found = <p className="text-sm text-red-600">Не удалось загрузить каталог</p>;
    else if (results.length === 0) found = <p className="text-sm text-gray-500">Товары не найдены</p>;
    else {
      found = (
        <ul className="max-h-56 divide-y overflow-y-auto rounded-md border bg-white" aria-label="Найденные товары">
          {results.map((product) => {
            const isSelected = selected.has(String(product.id));
            return (
              <li key={product.id}>
                <button
                  type="button"
                  disabled={disabled}
                  aria-pressed={isSelected}
                  data-testid={`${testId}-option-${product.id}`}
                  onClick={() => toggle(product)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-blue-50 disabled:opacity-50"
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                    {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-sm text-gray-900">{product.name}</span>
                    <span className="block text-xs text-gray-500">{product.sku || 'без артикула'}</span>
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-xs text-gray-600">
                    {product.price === null ? 'цена по запросу' : formatPrice(product.price, product.currency)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      );
    }
  }

  return (
    <div className="space-y-2" data-testid={testId}>
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Выбранные товары">
          {value.map((product) => (
            <li
              key={String(product.id)}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-blue-50 py-0.5 pl-2.5 pr-1 text-xs text-blue-900"
              data-testid={`${testId}-chip-${product.id}`}
            >
              <span className="truncate">{product.name}</span>
              <button
                type="button"
                aria-label={`Убрать: ${product.name}`}
                disabled={disabled}
                className="rounded-full p-0.5 hover:bg-blue-100 disabled:opacity-50"
                onClick={() => toggle(product)}
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          type="search"
          aria-label="Поиск товара"
          placeholder="Название или артикул"
          className="pl-9"
          value={input}
          disabled={disabled}
          onChange={(e) => setInput(e.target.value)}
        />
      </div>
      {found}
    </div>
  );
}
